import { useMemo } from "react";
import { axiosGet } from "../apis/useAxios";

// The logged-in user's rights (UserAccess/my-permissions), loaded after login and kept in sessionStorage:
// { isFullAccess, canAccessOutside, menus: [{ menuCode, menuName, menuGroup, sortOrder, icon, route, canView, canAdd, canEdit,
// canMapping }], groups: [{ groupName, sortOrder, icon, isTopLevel }] } — the sidebar is built from it.
// Only the SystemUser (UserType 1, backend team) has every right; Administrators have the rights ticked for them. The
// SystemUser and Administrators can change access rights. Until the rights are loaded (or when the API is not reachable)
// the website behaves as before: everything is allowed, so nobody is locked out by a missing call.

const KEY = "session_permissions";
/** Fired on window after the rights are (re)loaded. */
export const PERMISSIONS_EVENT = "permissions-loaded";
export const SYSTEM_USER_TYPE = 1;
export const ADMIN_USER_TYPE = 5;

/** The SystemUser (backend team): every right; the only one who sees Masters › App Menu. */
export const isSystemUser = () => Number(sessionStorage.getItem("session_userTypeId")) === SYSTEM_USER_TYPE;

/** The SystemUser or an Administrator: can change other users' access rights and the allowed networks. */
export const canManageRights = () => [SYSTEM_USER_TYPE, ADMIN_USER_TYPE].includes(Number(sessionStorage.getItem("session_userTypeId")));

export function readPermissions() {
  try {
    return JSON.parse(sessionStorage.getItem(KEY) || "null");
  } catch {
    return null;
  }
}

/** Loads the logged-in user's rights into sessionStorage; resolves to them (null when they could not be loaded). */
export async function loadMyPermissions() {
  try {
    const r = await axiosGet("UserAccess/my-permissions", { userName: sessionStorage.getItem("session_userName") ?? "" });
    if (!r?.isSuccess || !r.payload) return null;
    const p = r.payload;
    const value = {
      isFullAccess: Boolean(p.isFullAccess),
      canAccessOutside: Boolean(p.canAccessOutside),
      menus: (p.menus ?? []).map((m) => ({
        menuCode: m.menuCode,
        menuName: m.menuName ?? "",
        menuGroup: m.menuGroup ?? "",
        sortOrder: Number(m.sortOrder) || 0,
        icon: m.icon ?? "",
        route: (m.route || "").toLowerCase(),
        canView: Boolean(m.canView),
        canAdd: Boolean(m.canAdd),
        canEdit: Boolean(m.canEdit),
        canMapping: Boolean(m.canMapping),
      })),
      // Sidebar groups: { groupName, sortOrder, icon, isTopLevel } (App Menu › Groups).
      groups: (p.groups ?? []).map((g) => ({
        groupName: g.groupName ?? "",
        sortOrder: Number(g.sortOrder) || 0,
        icon: g.icon ?? "",
        isTopLevel: Boolean(g.isTopLevel),
      })),
    };
    sessionStorage.setItem(KEY, JSON.stringify(value));
    window.dispatchEvent(new Event(PERMISSIONS_EVENT)); // the sidebar rebuilds itself
    return value;
  } catch {
    return null;
  }
}

const RIGHT = { view: "canView", add: "canAdd", edit: "canEdit", mapping: "canMapping" };

/** can("hotels", "edit"); the SystemUser, an unknown screen or not-yet-loaded rights = allowed. */
export function can(menuCode, right = "view", permissions = readPermissions()) {
  if (!permissions || permissions.isFullAccess) return true;
  const menu = permissions.menus.find((m) => m.menuCode === menuCode);
  return menu ? Boolean(menu[RIGHT[right]]) : true;
}

/** The screen of a route ("/hotels?open=x" → hotels menu), longest route first; null when not a managed screen. */
export function menuOfPath(path, permissions = readPermissions()) {
  if (!permissions) return null;
  const p = String(path || "")
    .split("?")[0]
    .toLowerCase();
  return [...permissions.menus].sort((a, b) => b.route.length - a.route.length).find((m) => m.route && (p === m.route || p.startsWith(`${m.route}/`))) ?? null;
}

/**
 * The first screen (menu order) the user may view: { route, menuName }; Dashboard when rights are not loaded or for the
 * SystemUser; null when no screen is given to the user.
 */
export function firstOpenScreen(permissions = readPermissions()) {
  if (!permissions || permissions.isFullAccess) return { route: "/dashboard-overview", menuName: "Dashboard" };
  return permissions.menus.find((m) => m.canView && m.route) ?? null;
}

/** May the user open this route? Unknown routes stay open (login, dashboard sub pages, mobile view …). */
export const canOpenPath = (path, permissions = readPermissions()) => {
  if (!permissions || permissions.isFullAccess) return true;
  const menu = menuOfPath(path, permissions);
  return menu ? menu.canView : true;
};

/** The rights of one screen for a page: { view, add, edit, mapping }. */
export function usePermission(menuCode) {
  return useMemo(
    () => ({ view: can(menuCode, "view"), add: can(menuCode, "add"), edit: can(menuCode, "edit"), mapping: can(menuCode, "mapping") }),
    [menuCode],
  );
}
